import React from 'react';
import styled from 'styled-components';
import { StaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage, withArtDirection } from 'gatsby-plugin-image';
import { motion } from 'framer-motion';
import { Link } from 'components';
import { useIntersection } from 'hooks';

const TogetherIsBetter = () => {
  const [elRef, observerEntry] = useIntersection({ threshold: 0 });
  return (
    <Container>
      <div />
      <motion.div
        ref={elRef}
        initial={{ y: 100 }}
        animate={observerEntry.isIntersecting ? { opacity: 1, y: 0 } : { opacity: 0, y: 100 }}
        transition={{ duration: 0.5, delay: 0.25 }}>
        <StaticQuery
          query={graphql`
            {
              peopleInOfficeMobile: file(relativePath: { eq: "home/people_in_office_mobile.jpg" }) {
                name
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED
                    width: 671
                    placeholder: DOMINANT_COLOR
                    formats: [WEBP]
                  )
                }
              }
              peopleInOfficeTablet: file(relativePath: { eq: "home/people_in_office_tablet.jpg" }) {
                name
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED
                    width: 1343
                    placeholder: DOMINANT_COLOR
                    formats: [WEBP]
                  )
                }
              }
            }
          `}
          render={({ peopleInOfficeMobile, peopleInOfficeTablet }) => {
            const peopleInOfficeSources = withArtDirection(getImage(peopleInOfficeMobile), [
              {
                image: getImage(peopleInOfficeTablet),
                media: '(min-width: 48em)'
              }
            ]);
            return (
              <PeopleInOffice
                image={peopleInOfficeSources}
                alt="A busy office with people working at their desks and moving around the office"
                aria-hidden="true"
              />
            );
          }}
        />
        <h2>
          Healthcare Marketing
          <br aria-hidden="true" /> at its best
        </h2>
        <p>
          We offer Campaign Strategy, Activation and Brand Creative across Digital and Traditional
          Media. We work with local and international clients through our International Agency
          Membership of the Global Comms Network.
          <br />
          <br />
          Our Events and Conferences expertise includes In-Person, Hybrid and Online events for
          thought leadership, product launches and CPD learning.
        </p>
        <h2>
          Omnichannel. <br aria-hidden="true" />
          We&apos;re Specialists.
        </h2>
        <p>
          We have extensive experience working with compliance systems such as the IPHA Code of
          Practice and Veeva PromoMats.
          <br aria-hidden="true" />
          <Link to="/services/healthcare-brand-and-campaign-strategy/" invertLight>
            Find Out More
          </Link>
        </p>
      </motion.div>
      <div />
    </Container>
  );
};

const Container = styled.section`
  background: #fff;
  grid-template-rows: 10.875rem 1fr 6.25rem;
  > div:last-of-type {
    background-color: var(--ax-beige);
  }
  > div:first-of-type {
    grid-area: 1 / 1 / 2 / -1;
    background-color: white;
  }
  > div:last-of-type {
    grid-area: 3 / 1 / -1 / -1;
  }
  > div:nth-of-type(2) {
    background: var(--ax-gold);
    box-shadow: 0.438rem 0.438rem 1.875rem rgba(0, 0, 0, 0.2);
    grid-area: 1 / 2 / -1 / 3;
    padding: 1.85rem 1.25rem 1.55rem;
    margin-bottom: 1.875rem;
    margin-top: -5rem;
    position: relative;
    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.15);
      z-index: 1;
    }
    h2:not(.big-background-section-title),
    p {
      position: relative;
      color: #fff;
      display: flex;
      flex-direction: column;
      gap: 1.875rem;
      z-index: 2;
    }
    h2:not(.big-background-section-title) {
      font-family: 'Butler', serif;
      font-display: auto;
      font-size: 3rem;
      font-weight: 400;
      left: 0.021em;
      letter-spacing: 0.013em;
      line-height: 1.25em;
    }
    p {
      font-size: 1.25rem;
      left: 0.175em;
      letter-spacing: 0.001em;
      line-height: 1.6em;
      margin: 0.5em 0 2.925em;
      &:last-of-type {
        margin: 0.8em 0 0em;
      }
    }
  }
  @media screen and (min-width: 48rem) {
    grid-template-rows: 4.063rem 1fr 7.5rem;
    > div:nth-of-type(2) {
      grid-area: 1 / 1 / -1 / -1;
      margin: 0 1.25rem 1.875rem;
      margin-top: -5rem;
      padding: 3.25rem 2.2rem 3rem;
      p {
        margin: 1.75em 0 4.25em;
        font-size: 1.25rem;
        &:last-of-type {
          margin: 1.75em 0 0em;
        }
      }
    }
  }
  @media screen and (min-width: 62.5rem) {
    > div:nth-of-type(2) {
      margin-bottom: 4.3rem;
    }
  }
  @media screen and (min-width: 76.875rem) {
    > div:nth-of-type(2) {
      display: grid;
      grid-template-rows: minmax(184px, auto) minmax(118px, auto);
      grid-gap: 32px 6.4%;
      justify-content: center;
      padding: 7.9rem 13.47%;
      margin: 0 3rem 4.3rem;
      margin-top: -5rem;
      h2:not(.big-background-section-title):first-of-type {
        grid-area: 1 / 1;
      }
      h2:not(.big-background-section-title):last-of-type {
        grid-area: 2 / 1;
      }
      p {
        margin: 0 !important;
      }
      p:first-of-type {
        grid-area: 1 / 2;
      }
      p:last-of-type {
        grid-area: 2 / 2;
      }
    }
  }
  @media screen and (min-width: 114.75em) {
    > div:nth-of-type(2) {
      max-width: 108.75rem;
      margin: 0 auto 4.3rem;
      margin-top: -5rem;
    }
  }
`;

const PeopleInOffice = styled(GatsbyImage)`
  height: 100%;
  left: 0;
  position: absolute !important;
  top: 0;
  width: 100%;
`;

export default TogetherIsBetter;
